/**
 * ステップ見積もりのプリセットストリップ。
 *
 * 構成は PHRASE BLOOM の PresetBar と同じ
 *   [保存] [プリセット名（クリックでリネーム）▼] [<] [>] [削除]
 * だが、シンセパネルではなく通常ページ（白背景）に合わせた見た目にしている。
 * ロジックは汎用フック `useToolPresetBrowser` に委譲。
 * 1 プリセット = 1 つの項目表。見積もり表は営業情報を含むため共有せず、
 * 保存した本人のみが見える（shareOnSave / showSharedSources とも false）。
 *
 * 共有ソースが無いツールではドロップダウンの所有者階層に意味が無いので、
 * ソースが 1 つだけのときは自分の表をフラットに並べる。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import ConfirmModal from "../../../../components/modal/ConfirmModal";
import ToolTip from "../../../../components/tooltip/ToolTip";
import {
  useToolPresetBrowser,
  type UseToolPresetBrowserArgs,
} from "../../hooks/useToolPresetBrowser";

// ===== スタイル =====

const StripButton = styled.button<{ $lit?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  color: ${({ $lit }) => ($lit ? "var(--soremo-blue)" : "var(--soremo-light-gray)")};
  cursor: pointer;
  transition: color 0.15s ease-out;

  .material-symbols-rounded {
    font-size: 20px;
  }

  &:hover:not(:disabled) {
    color: var(--soremo-blue);
    border-color: var(--soremo-blue);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

const Display = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 180px;
  max-width: 360px;
  height: 34px;
  padding: 0 4px 0 10px;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
`;

const NameButton = styled.button.attrs({ className: "bodytext-13" })`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: text;
`;

const NameInput = styled.input.attrs({ className: "bodytext-13" })`
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  padding: 0;

  &:focus {
    outline: none;
  }
`;

const CaretButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--soremo-light-gray);
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 20px;
  }

  &:hover {
    color: var(--soremo-blue);
  }
`;

const Menu = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-height: 320px;
  overflow-y: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  box-shadow: 2px 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 20;
`;

const MenuItem = styled.li.attrs({ className: "bodytext-13" })<{
  $active?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  white-space: nowrap;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 16px;
    color: ${({ $active }) => ($active ? "var(--soremo-blue)" : "transparent")};
  }

  &:hover {
    background: var(--soremo-background);
  }
`;

const SourceItem = styled(MenuItem)`
  justify-content: space-between;

  > .material-symbols-rounded:last-child {
    color: var(--soremo-light-gray);
  }
`;

const PresetItem = styled(MenuItem)`
  padding-left: 34px;
`;

const EmptyItem = styled.li.attrs({ className: "bodytext-11" })<{
  $nested?: boolean;
}>`
  padding: 8px 14px;
  padding-left: ${({ $nested }) => ($nested ? 34 : 14)}px;
  color: var(--soremo-light-gray);
`;

const SaveGroup = styled.div`
  position: relative;
`;

const TooltipTarget = styled.span`
  display: inline-flex;
`;

// ===== 本体 =====

export type PresetStripProps = Omit<
  UseToolPresetBrowserArgs,
  "shareOnSave" | "showSharedSources"
>;

const PresetStrip: React.FC<PresetStripProps> = (props) => {
  const browser = useToolPresetBrowser({
    ...props,
    // 見積もり表は本人のみ（相互フォローへの共有を行わない）
    shareOnSave: false,
    showSharedSources: false,
  });

  const [openMenu, setOpenMenu] = useState<"source" | "save" | null>(null);
  const [expandedSourceId, setExpandedSourceId] = useState<"me" | number | null>(
    null,
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!openMenu) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [openMenu]);

  const beginRename = useCallback(() => {
    setNameDraft(browser.name);
    setIsEditingName(true);
  }, [browser.name]);

  const commitRename = useCallback(() => {
    setIsEditingName(false);
    browser.rename(nameDraft);
  }, [browser, nameDraft]);

  const handleSaveClick = useCallback(() => {
    if (!browser.isSavable || browser.isSaving) return;
    if (browser.ownLoaded) {
      setOpenMenu((prev) => (prev === "save" ? null : "save"));
    } else {
      browser.saveAsNew();
    }
  }, [browser]);

  /** 共有ソースが無い（自分だけ）ときの、階層なしのプリセット一覧。 */
  const renderFlatPresets = () => {
    const source = browser.sources[0];
    if (source.presets.length === 0) {
      return <EmptyItem>保存された表はありません</EmptyItem>;
    }
    return source.presets.map((preset) => (
      <MenuItem
        key={preset.id}
        $active={preset.id === browser.currentPresetId}
        onClick={() => {
          setOpenMenu(null);
          browser.selectPreset(source.id, preset.id);
        }}
      >
        <span className="material-symbols-rounded">check</span>
        {preset.name}
      </MenuItem>
    ));
  };

  /** 共有ソースがあるときの、所有者 → プリセットの 2 階層表示。 */
  const renderGroupedPresets = () =>
    browser.sources.map((source) => {
      const expanded = expandedSourceId === source.id;
      return (
        <React.Fragment key={String(source.id)}>
          <SourceItem
            $active={source.id === browser.sourceId}
            onClick={() => setExpandedSourceId(expanded ? null : source.id)}
          >
            <span className="u-row u-gap8 u-align-center">
              <span className="material-symbols-rounded">check</span>
              {source.id === "me" ? "自分の表" : source.label}
            </span>
            <span className="material-symbols-rounded">
              {expanded ? "expand_more" : "chevron_right"}
            </span>
          </SourceItem>

          {expanded &&
            (source.presets.length > 0 ? (
              source.presets.map((preset) => (
                <PresetItem
                  key={preset.id}
                  $active={
                    source.id === browser.sourceId &&
                    preset.id === browser.currentPresetId
                  }
                  onClick={() => {
                    setOpenMenu(null);
                    browser.selectPreset(source.id, preset.id);
                  }}
                >
                  <span className="material-symbols-rounded">check</span>
                  {preset.name}
                </PresetItem>
              ))
            ) : (
              <EmptyItem $nested>保存された表はありません</EmptyItem>
            ))}
        </React.Fragment>
      );
    });

  return (
    <div ref={rootRef} className="u-row u-gap8 u-align-center">
      <SaveGroup>
        <ToolTip
          description={
            browser.ownLoaded ? "保存（上書き / 新規）" : "自分の表として保存"
          }
        >
          <TooltipTarget>
            <StripButton
              type="button"
              $lit={browser.isSavable}
              disabled={!browser.isSavable || browser.isSaving}
              aria-label="表を保存"
              onClick={handleSaveClick}
            >
              <span className="material-symbols-rounded">save</span>
            </StripButton>
          </TooltipTarget>
        </ToolTip>

        {openMenu === "save" && (
          <Menu>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                browser.overwrite();
              }}
            >
              <span className="material-symbols-rounded">check</span>
              上書き保存
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                browser.saveAsNew();
              }}
            >
              <span className="material-symbols-rounded">check</span>
              新しい表として保存
            </MenuItem>
          </Menu>
        )}
      </SaveGroup>

      <Display>
        {isEditingName ? (
          <NameInput
            autoFocus
            value={nameDraft}
            maxLength={100}
            aria-label="表の名前"
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />
        ) : (
          <ToolTip description="クリックで名前を変更">
            <NameButton
              type="button"
              aria-label={`${browser.name}の名前を変更`}
              onClick={beginRename}
            >
              {browser.name}
            </NameButton>
          </ToolTip>
        )}

        <ToolTip description="保存済みの表を選択">
          <CaretButton
            type="button"
            aria-label="保存済みの表を選択"
            onClick={() =>
              setOpenMenu((prev) => {
                if (prev === "source") return null;
                setExpandedSourceId(browser.sourceId);
                return "source";
              })
            }
          >
            <span className="material-symbols-rounded">arrow_drop_down</span>
          </CaretButton>
        </ToolTip>

        {openMenu === "source" && (
          <Menu>
            {browser.sources.length <= 1
              ? renderFlatPresets()
              : renderGroupedPresets()}
          </Menu>
        )}
      </Display>

      <ToolTip description="前の表">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.hasPresets}
            aria-label="前の表"
            onClick={browser.goPrev}
          >
            <span className="material-symbols-rounded">chevron_left</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>
      <ToolTip description="次の表">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.hasPresets}
            aria-label="次の表"
            onClick={browser.goNext}
          >
            <span className="material-symbols-rounded">chevron_right</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>

      <ToolTip description="この表を削除">
        <TooltipTarget>
          <StripButton
            type="button"
            disabled={!browser.ownLoaded || browser.isDeleting}
            aria-label="表を削除"
            onClick={() => setConfirmingDelete(true)}
          >
            <span className="material-symbols-rounded">delete</span>
          </StripButton>
        </TooltipTarget>
      </ToolTip>

      {confirmingDelete && (
        <ConfirmModal
          message={`「${browser.name}」を削除しますか？`}
          confirmLabel="削除"
          onConfirm={() => {
            setConfirmingDelete(false);
            browser.deleteCurrent();
          }}
          onCancel={() => setConfirmingDelete(false)}
          loading={browser.isDeleting}
        />
      )}
    </div>
  );
};

export default PresetStrip;
